/* @flow */
import { partition } from 'lodash';
import * as React from 'react';

import Button from 'components/ButtonDeprecated';
import Hovercard from 'components/Hovercard';
import HovercardBody from 'components/HovercardBody';
import Image from 'components/Image';
import Icon from 'components/Icon';
import type { IconName } from 'components/Icon';
import LoadingIndicator from 'components/LoadingIndicator';
import ReduxUploadButton from 'components/ReduxUploadButton';
import TooltipTrigger from 'components/TooltipTrigger';
import EventTracking from 'lib/EventTracking';
import { MODAL_FILE_CONTEXT_KEY } from 'reducers/fileUploads';
import { diffSourcesByLinked, showAccountLinkingToastsForDiff } from 'utils/accountLinkingToast';
import openPopup from 'utils/openPopup';
import wait from 'utils/wait';

import { settingsForProvider } from '../../../components/lib/AccountLinkingWindowSettings';
import './styles/ComposerIntegrationsServices.scss';

const className = 'ComposerIntegrationsServices';

// if a service has no linkedAccountInfo, assume it doesn't need to be linked
const isAccountUnlinked = service =>
  service.linkedAccountInfo && !service.linkedAccountInfo.isLinked;

type ContentSourceLinkedAccount = {
  authUrl: string,
  providerKey: string,
  isLinked: boolean,
};

export type ContentSource = {
  title: string,
  providerKey: string,
  iconURL: string,
  linkedAccountInfo?: ContentSourceLinkedAccount,
};

type Props = {
  error?: Error,
  loading: boolean,
  sources?: ?Array<ContentSource>,
  onViewContent: (contentSource: *) => void,
  onAttachFile: () => void,
  reloadContentSources: void => void,
  /* Used to determine whether to render the tooltip on the disabled file upload buttons;
   * set based on |fileUploads| from redux state, and passed in from VisualComposer.
   * ReduxUploadButton reads the same state to determine whether to disable itself, but
   * our TooltipTrigger must be attached to a component above it in the tree, or else the
   * tooltip and button will start a render loop & cause the tooltip to flicker. */
  hasFile?: boolean,
};

type State = {
  linkingSource: *,
};

class ComposerIntegrationsServices extends React.Component<Props, State> {
  state = {
    linkingSource: null,
  };

  componentWillUpdate(nextProps: Props) {
    ((oldSources, newSources) => {
      if (!oldSources || !newSources) {
        return;
      }

      const diff = diffSourcesByLinked(oldSources, newSources);
      showAccountLinkingToastsForDiff(diff);

      if (this.state.linkingSource && diff.newlyLinked[this.state.linkingSource.providerKey]) {
        this.props.onViewContent(this.state.linkingSource);
      }
    })(this.props.sources, nextProps.sources);
  }

  linkAccount(service: *) {
    // if not linkable account or already linked, show content
    if (!isAccountUnlinked(service)) {
      this.props.onViewContent(service);
      EventTracking.trackKey('account_linking.composer.content_provider.click', {
        provider: service.providerKey,
        composer_version: 'web_full_page',
      });
    } else {
      // otherwise link the account
      const popup = openPopup(
        `${service.linkedAccountInfo.authUrl}`,
        `Link with ${service.title}`,
        settingsForProvider(service.linkedAccountInfo.providerKey)
      );

      wait(() => popup?.closed).then(this.props.reloadContentSources, e => {
        console.debug(e, e && e.stack);
        console.error('Error while waiting for account linking popup to close');
        // Reload content sources anyway - hope for the best
        this.props.reloadContentSources();
      });

      EventTracking.trackKey('account_linking.composer.connect.click', {
        provider: service.providerKey,
        composer_version: 'web_full_page',
      });

      this.setState({
        linkingSource: service,
      });
    }
  }

  renderUploadButton(text: string, iconName: IconName, eventKey: string) {
    const component = (
      <span className={`${className}-item`}>
        <ReduxUploadButton
          className={`${className}-button`}
          fileContextKey={MODAL_FILE_CONTEXT_KEY}
          eventKey={eventKey}
          eventProperties={{
            composer_version: 'web_full_page',
          }}
          onUploadStart={this.props.onAttachFile}
          level="primary"
          weight="normal">
          <div className={`${className}-item-icon`}>
            <Icon name={iconName} size="medium" />
          </div>
          <span className={`${className}-item-label`}>{text}</span>
        </ReduxUploadButton>
      </span>
    );
    return this.props.hasFile ? (
      <TooltipTrigger
        tooltip={'You can upload one file per message'}
        placement="top"
        alignment="left">
        {component}
      </TooltipTrigger>
    ) : (
      component
    );
  }

  renderServiceButton = (service: *) => {
    return (
      <span className={`${className}-item`} key={service.providerKey}>
        <Button
          className={`${className}-button`}
          level="primary"
          weight="normal"
          data-test="service-button"
          onClick={() => this.linkAccount(service)}
          eventElementName={`${className}-button-${service.providerKey}`}>
          <div className={`${className}-item-icon`}>
            <Image src={service.iconURL} className={`${className}-item-icon-image`} />
          </div>
          <div className={`${className}-item-label`}>
            <span className={`${className}-item-label-text`}>{service.title}</span>
            {isAccountUnlinked(service) && (
              <Icon
                className={`${className}-item-label-plus`}
                size="medium"
                name="plus-circle"
                color="mascot"
              />
            )}
          </div>
        </Button>
      </span>
    );
  };

  renderServices(): React.Element | Array<React.Element> | null {
    const { sources, loading } = this.props;

    if (loading) {
      return <LoadingIndicator className={`${className}-loading`} align="center" />;
    }

    if (sources) {
      const [unlinked, linked] = partition(sources, isAccountUnlinked);
      return [...linked, ...unlinked].map(this.renderServiceButton);
    } else {
      return null;
    }
  }

  render() {
    return (
      <Hovercard {...this.props} className={className}>
        <HovercardBody className={`${className}-body`}>
          {this.renderUploadButton(
            'Photo or video',
            'image-stroked',
            'announcements.announcement_modal.universal_composer.upload_photo.click'
          )}
          {this.renderUploadButton(
            'File',
            'paperclip-stroked',
            'announcements.announcement_modal.universal_composer.upload_file.click'
          )}
          {this.renderServices()}
        </HovercardBody>
      </Hovercard>
    );
  }
}

export { ComposerIntegrationsServices };
export default ComposerIntegrationsServices;
